import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { fetchBackendJson } from './backend/api'

const REFRESH_MS = 60_000
// The backend refreshes its ranking daily; polling this often picks a refresh up without tracking its schedule.
const TRENDING_REFRESH_MS = 30 * 60_000

export const TOKEN_LIST_BY_VOLUME_QUERY_KEY = 'tokenListByVolume'
export const TRENDING_TOKENS_QUERY_KEY = 'trendingTokens'

type ServedTokenList = {
  tokens: {
    address: string
    extensions?: { volume24hUsd?: number }
  }[]
}

export type TokenMarketStats = { volume24hUsd?: number }

/** The part of the served list the selector uses, kept small since it is persisted. */
type VolumeEntry = [address: string, volume24hUsd: number | null]

async function fetchVolumes(chainId: number): Promise<VolumeEntry[]> {
  const { tokens } = await fetchBackendJson<ServedTokenList>(`/token-list?chainId=${chainId}&sort=volume`)
  return tokens.map(({ address, extensions }) => [address.toLowerCase(), extensions?.volume24hUsd ?? null])
}

async function fetchTrending(chainId: number): Promise<string[]> {
  const { tokens } = await fetchBackendJson<{ tokens: string[] }>(`/token-list/trending?chainId=${chainId}`)
  return tokens
}

/** Query options shared by the hook and background prefetching. */
export function tokenVolumesQueryOptions(chainId: number | undefined) {
  return {
    queryKey: [TOKEN_LIST_BY_VOLUME_QUERY_KEY, chainId] as const,
    queryFn: () => fetchVolumes(chainId as number),
    staleTime: REFRESH_MS,
  }
}

/** Query options shared by the hook and background prefetching. */
export function trendingTokensQueryOptions(chainId: number | undefined) {
  return {
    queryKey: [TRENDING_TOKENS_QUERY_KEY, chainId] as const,
    queryFn: () => fetchTrending(chainId as number),
    staleTime: TRENDING_REFRESH_MS,
  }
}

/**
 * Market activity of the chain's tokens, by lowercase address: 24h volume of
 * every listed token, and the trending rank (1 = hottest) of trending ones.
 */
export function useTrendingTokens(chainId: number | undefined) {
  const volumes = useQuery({
    ...tokenVolumesQueryOptions(chainId),
    enabled: Boolean(chainId),
    refetchInterval: REFRESH_MS,
  })
  const trending = useQuery({
    ...trendingTokensQueryOptions(chainId),
    enabled: Boolean(chainId),
    refetchInterval: TRENDING_REFRESH_MS,
    // Optional enhancement: a backend without the ranking must not stall the list behind retries.
    retry: false,
  })

  const isLoading = volumes.isLoading
  return useMemo(() => {
    const stats = new Map<string, TokenMarketStats>(
      (volumes.data ?? []).map(([address, volume]) => [address, { volume24hUsd: volume ?? undefined }])
    )
    const ranks = new Map((trending.data ?? []).map((address, index) => [address.toLowerCase(), index + 1]))
    return { stats, ranks, isLoading }
  }, [volumes.data, trending.data, isLoading])
}
